import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context.js';
import { ContentFormLayout } from './ContentFormLayout.js';
import { DraftBanner } from './DraftBanner.js';
import { useContentForm } from './useContentForm.js';
import type { ContentApi, ContentRecord } from './content-api.js';

export type ContentEditContext<TForm> = {
  form: TForm;
  setForm: (next: TForm | ((current: TForm) => TForm)) => void;
  fieldErrors: Record<string, string>;
  record: ContentRecord | null;
  isNew: boolean;
};

export type ContentEditPageProps<TForm> = {
  collection: string;
  contentApi: ContentApi<ContentRecord>;
  basePath: string;
  backLabel: string;
  /** «Yangi yangilik», «Yangi xizmat». */
  newTitle: string;
  empty: () => TForm;
  fromRecord: (record: ContentRecord) => TForm;
  toInput: (form: TForm) => Record<string, unknown>;
  /** Yuqoridagi sarlavha uchun. */
  titleOf: (form: TForm) => string;
  renderFields: (context: ContentEditContext<TForm>) => ReactNode;
  /** Sahifasi bo'lmagan bo'limlarda «Ko'rish» tugmasi ko'rsatilmaydi. */
  previewable?: boolean;
};

/**
 * Kontent tahrirlash ekrani — 8 ta bo'lim uchun bitta karkas.
 *
 * Sahifalar (`PageEditPage`) alohida yozilgan: unda blok konstruktori bor
 * va ekran boshqa tuzilishda. Qolganlari faqat maydonlardan iborat.
 */
export function ContentEditPage<TForm>({
  collection,
  contentApi,
  basePath,
  backLabel,
  newTitle,
  empty,
  fromRecord,
  toInput,
  titleOf,
  renderFields,
  previewable = true,
}: ContentEditPageProps<TForm>) {
  const { id = 'new' } = useParams();
  const { user } = useAuth();

  const form = useContentForm<TForm>({
    collection,
    contentApi,
    id,
    basePath,
    empty,
    fromRecord,
    toInput,
  });

  if (form.loading) {
    return <p className="p-6 text-sm text-n-600">Yuklanmoqda…</p>;
  }

  if (form.notFound) {
    return <p className="p-6 text-sm text-error-600">Yozuv topilmadi yoki o‘chirilgan.</p>;
  }

  return (
    <ContentFormLayout
      title={titleOf(form.form) || (form.isNew ? newTitle : '(nomsiz)')}
      backTo={basePath}
      backLabel={backLabel}
      isNew={form.isNew}
      isPublished={form.isPublished}
      saving={form.saving}
      publishing={form.publishing}
      canPublish={user?.role !== 'editor'}
      autosave={form.autosave}
      lock={form.lock}
      unsaved={form.unsaved}
      onSave={form.save}
      onTogglePublish={form.togglePublish}
      {...(previewable ? { onPreview: form.preview } : {})}
    >
      <div className="space-y-4 p-4 sm:p-6">
        <DraftBanner
          savedAt={form.pendingDraft ? (form.record?.draftAt ?? null) : null}
          onRestore={form.restoreDraft}
          onDiscard={form.discardDraft}
        />

        {renderFields({
          form: form.form,
          setForm: form.setForm,
          fieldErrors: form.fieldErrors,
          record: form.record,
          isNew: form.isNew,
        })}
      </div>
    </ContentFormLayout>
  );
}
